import { simplify, stringify, evaluate, type ExprNode } from '@learnforge/demo-math/expr';
import { findExtrema } from './extrema';
import { findZerosBisection } from './zeros';

interface PolynomialCoeffs {
  degree: number;
  leading: number;
}

function extractPolynomialCoeffs(node: ExprNode): PolynomialCoeffs | null {
  try {
    const simplified = simplify(node);
    const src = stringify(simplified);
    const matches = [...src.matchAll(/x\s*\^\s*(\d+)/g)];
    let maxDegree = matches.length > 0 ? Math.max(...matches.map((m) => Number(m[1] ?? 0))) : 0;
    if (maxDegree === 0 && /(?<![a-zA-Z])x(?![\^\d])/.test(src)) maxDegree = 1;
    if (maxDegree === 0 && /^\s*[-+]?[\d.]+\s*$/.test(src)) maxDegree = 0;
    if (maxDegree < 0 || maxDegree > 6) return null;

    const xLarge = 1e6;
    const y = evaluate(node, { x: xLarge });
    if (typeof y !== 'number' || !Number.isFinite(y)) return null;
    if (maxDegree === 0) return { degree: 0, leading: y };
    const leading = y / Math.pow(xLarge, maxDegree);
    return { degree: maxDegree, leading };
  } catch {
    return null;
  }
}

const PARABOLA_PHRASINGS_UP = [
  'a parabola opening upward',
  'an upward-opening parabola',
  'a U-shaped curve',
];
const PARABOLA_PHRASINGS_DOWN = [
  'a parabola opening downward',
  'a downward-opening parabola',
  'an inverted U-shape',
];

export function generateVerbalDescription(node: ExprNode): string {
  const poly = extractPolynomialCoeffs(node);
  if (poly) {
    if (poly.degree === 0) return `a constant function with value ${poly.leading.toFixed(3)}`;
    if (poly.degree === 1) return `a linear function with slope ${poly.leading.toFixed(3)}`;
    if (poly.degree === 2) {
      // Use a fixed phrasing for test determinism; the alternative phrasings remain for future variation.
      const head =
        poly.leading > 0
          ? (PARABOLA_PHRASINGS_UP[0] as string)
          : (PARABOLA_PHRASINGS_DOWN[0] as string);
      const ex = findExtrema(node, -100, 100);
      const zeros = findZerosBisection(node, -100, 100);
      const firstEx = ex[0];
      const vertex = firstEx
        ? ` with vertex at (${firstEx.x.toFixed(2)}, ${firstEx.y.toFixed(2)})`
        : '';
      const zerosText =
        zeros.length > 0 ? `; zeros at ${zeros.map((z) => z.toFixed(2)).join(', ')}` : '';
      return `${head}${vertex}${zerosText}`;
    }
    return `a degree-${poly.degree} polynomial`;
  }
  return 'an arbitrary function on this domain';
}
